import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface LayoutFrameState {
  appBarHidden: boolean;
  secondaryNavHidden: boolean;
  canvasToolbarHidden: boolean;
}

const defaultState: LayoutFrameState = {
  appBarHidden: false,
  secondaryNavHidden: false,
  canvasToolbarHidden: false,
};

type LayoutFrameContextValue = LayoutFrameState & {
  setFrameVisibility: (partial: Partial<LayoutFrameState>) => void;
  /** Sets all layout visibility flags back to visible. */
  resetFrameVisibility: () => void;
};

const LayoutFrameContext = createContext<LayoutFrameContextValue | null>(null);

export function LayoutFrameProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LayoutFrameState>(defaultState);

  const setFrameVisibility = useCallback((partial: Partial<LayoutFrameState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetFrameVisibility = useCallback(() => {
    setState(defaultState);
  }, []);

  const value = useMemo(
    () => ({ ...state, setFrameVisibility, resetFrameVisibility }),
    [state, setFrameVisibility, resetFrameVisibility],
  );

  return (
    <LayoutFrameContext.Provider value={value}>
      {children}
    </LayoutFrameContext.Provider>
  );
}

export function useLayoutFrame(): LayoutFrameContextValue {
  const ctx = useContext(LayoutFrameContext);
  if (!ctx) {
    throw new Error('useLayoutFrame must be used within LayoutFrameProvider');
  }
  return ctx;
}
